/*
  art-forms — gemeinsame Logik für alle art-Formulare (Kontakt, B2B, später Druckdaten).

  GRUNDSATZ: Verbesserung, nicht Ersatz. Das Markup bleibt ein echtes `{% form 'contact' %}`.
  Dieses Skript fängt das Absenden nur ab, wenn es sich vorher überzeugt hat, dass der eigene
  Endpunkt erreichbar ist. Fällt JavaScript aus, fehlt der Endpunkt oder antwortet er mit einem
  Fehler, läuft der native Shopify-Weg — es gibt damit nie ein totes Formular.

  EIN Weg, nicht zwei (seit 02.10.2026): die Benachrichtigungs-Mail verschickt das DASHBOARD.
  Hier wird nichts mehr zusätzlich an Shopify geschickt. Die Begründung steht ausführlich beim
  Abschnitt „Benachrichtigungs-Mail" weiter unten — kurz: Shopifys Kontaktformular hängt hinter
  hCaptcha und ist aus dem Hintergrund nicht zu bedienen. Bleibt der eigene Eingang stumm, geht
  das Formular weiterhin NATIV an Shopify; die Nachricht kommt dann als Mail an, nur ohne
  Eintrag. Datenverlust gibt es auf keinem Weg.

  Warum alles über `data-`-Attribute hereinkommt: ein `.js`-Asset erreicht weder die
  Sprachdateien noch die Theme-Einstellungen. Also liefert Liquid Endpunkt, Shop-Kennung,
  Grenzen und JEDEN Text am Element ab (dokumentiertes Muster in diesem Repo).

  Vertrag mit dem Dashboard: `docs/formular-backend-plan.md`. Kurz:
    POST {endpoint}/api/public/forms/{shop}                     → Eintrag anlegen (201/200)
    GET  {endpoint}/api/public/forms/health                     → Vorabprüfung
    POST {endpoint}/api/public/forms/{shop}/upload?key=&name=   → Datei, roher Rumpf
    POST {endpoint}/api/public/forms/{shop}/filedrop            → Uploadlink für zu große Dateien
  Antwort des Eintrags: { ok, id, url, attachments: { taken, rejected } }
  `url` nimmt das Dashboard für seine Mail; `rejected` nennt die Dateien, die der Eingang
  NICHT angenommen hat — daraus wird die Teilerfolg-Meldung für den Kunden gebaut.
*/

(() => {
  const HOSTS = new WeakSet();
  /** Vorabprüfung je Endpunkt nur EINMAL, auch bei mehreren Formularen auf der Seite. */
  const HEALTH = new Map();

  const t = (el, name, fallback) => (el.dataset[name] || '').trim() || fallback || '';

  function health(endpoint) {
    if (!HEALTH.has(endpoint)) {
      HEALTH.set(
        endpoint,
        /* Nicht „irgendwas hat 200 geantwortet", sondern „UNSER Eingang hat geantwortet":
           steht in Produktion versehentlich noch Cloudflare Access davor, kommt eine
           Login-Seite statt JSON. Deshalb wird der Rumpf gelesen. Schlägt das fehl, gilt der
           Eingang als nicht vorhanden — und der Anhang-Bereich bleibt unsichtbar, statt die
           Datei des Kunden zu verschlucken.

           Die Antwort trägt zugleich die GRENZEN (Größe, Endungen, Gültigkeit des
           Uploadlinks). Die stehen bewusst nur dort und nicht zusätzlich in einer
           Theme-Einstellung — zwei Orte für dieselbe Zahl laufen auseinander. */
        fetch(endpoint + '/api/public/forms/health', { method: 'GET', mode: 'cors' })
          .then((r) => (r.ok ? r.json() : null))
          .then((j) => (j && j.ok === true ? j : null))
          .catch(() => null)
      );
    }
    return HEALTH.get(endpoint);
  }

  function newKey() {
    if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
    // Rückfall für alte Browser: reicht als Idempotenz-Schlüssel (nur Doppelklick-Schutz).
    return 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 12);
  }

  /* Der Schlüssel eines Feldes ist seine BESCHRIFTUNG — dann zeigt der Eintrag im Dashboard
     dieselben Worte wie das Formular, und im Code steht kein deutscher Schlüssel. Reihenfolge:
     ausdrückliche Angabe, sonst das zugehörige <label>, sonst der Name in den Klammern. */
  /* Das Pflichtfeld-Sternchen gehört zur ANZEIGE, nicht zum Namen des Feldes: im Markup
     steht es als eigenes `<span aria-hidden="true">*</span>` neben der Beschriftung, und
     Vorlesewerkzeuge überspringen es schon heute. Im Dashboard-Eintrag und in der
     Meldungs-Mail hat es nichts verloren — dort ist nichts mehr auszufüllen (Alex,
     07.10.2026). Nur am ENDE kürzen; ein Stern mitten im Text bleibt stehen. */
  const ohneStern = (s) => s.replace(/[\s*]+$/, '').trim() || s.trim();

  function fieldKey(form, el) {
    if (el.dataset.artFormLabel) return ohneStern(el.dataset.artFormLabel);
    if (el.id) {
      const lab = form.querySelector('label[for="' + CSS.escape(el.id) + '"]');
      const txt = lab && lab.textContent ? lab.textContent.replace(/\s+/g, ' ').trim() : '';
      if (txt) return ohneStern(txt);
    }
    const m = /^contact\[(.+)\]$/.exec(el.name || '');
    return ohneStern(m ? m[1] : el.name || '');
  }

  /* Felder, die das FORMULAR braucht, ein Mensch aber nicht. Sie standen bis zum
     07.10.2026 mitten in den Angaben des Dashboard-Eintrags und in der Meldungs-Mail
     („form_type: contact", „utf8: ✓" — vom Owner gemeldet).

     ⚠️ `cf-turnstile-response` ist der wichtigste Eintrag: das Widget legt sein
     verstecktes Feld IM Formular ab. Solange die Bot-Prüfung erst beim Klick auf
     „Senden" lief, war es beim Einsammeln noch leer und fiel von selbst heraus — seit
     sie beim ersten Tippen anläuft, steht dort ein rund 2000 Zeichen langer Token.

     Entfernt werden sie zusätzlich im Eingang: welche Fassung dieser Datei eine
     Storefront ausliefert, hat das Dashboard nicht in der Hand. */
  const MASCHINENFELDER = new Set([
    'form_type', // Shopifys {% form %}: welches Formular (immer „contact")
    'utf8', // Shopifys Kodierungsprobe (immer „✓")
    'contact[id]', // Shopifys Formular-Kennung
    'cf-turnstile-response', // unser Turnstile-Token
    'g-recaptcha-response',
    'h-captcha-response', // Shopifys eigener Bot-Schutz
  ]);

  function collect(form) {
    const fields = {};
    const roles = {};
    let consent = false;

    for (const el of form.elements) {
      /* Auf VORHANDENSEIN prüfen, nicht auf Wahrheit: ein Attribut ohne Wert
         (`data-art-form-consent`) liefert den leeren String, und der ist falsy — die
         Zustimmung wäre sonst lautlos aus dem Rumpf gefallen. */
      const hasRole = el.dataset.artFormRole !== undefined;
      const isConsent = el.dataset.artFormConsent !== undefined;
      if (!el.name && !hasRole && !isConsent) continue;
      if (el.dataset.artFormHoneypot !== undefined) continue;
      if (MASCHINENFELDER.has(el.name) || el.type === 'submit' || el.type === 'button') continue;

      if (isConsent) {
        consent = !!el.checked;
        continue;
      }
      if (el.type === 'checkbox' && !el.checked) continue;
      if (el.type === 'file') continue;

      const val = (el.value || '').trim();
      if (hasRole && el.dataset.artFormRole) roles[el.dataset.artFormRole] = val;
      if (val && el.name) fields[fieldKey(form, el)] = val;
    }
    return { fields, roles, consent };
  }

  /* Die Meldung kann einen Link tragen — gebraucht, wenn eine Datei nicht durchkam und der
     Kunde sie über den Uploadlink nachreichen soll. Ein Hinweis ohne Ausweg ist eine
     Sackgasse, und der Link steht dann direkt in der Meldung statt nur unter dem Feld. */
  function setStatus(host, text, tone, links) {
    const box = host.querySelector('[data-art-form-status]');
    if (!box) return;
    const list = (links || []).filter((l) => l && l.url);
    box.innerHTML = '';
    box.textContent = text || '';
    box.hidden = !text && list.length === 0;
    box.dataset.tone = tone || 'info';
    for (const l of list) {
      box.appendChild(document.createTextNode(' ' + (l.prefix ? l.prefix + ' ' : '')));
      const a = document.createElement('a');
      a.href = l.url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = l.label || l.url;
      box.appendChild(a);
    }
  }

  /* ── Anhänge ────────────────────────────────────────────────────────────────────────
     Ablauf (Vorgabe Alex, 24.08.2026):
       • Auswählen prüft NUR — hochgeladen wird beim ABSENDEN. Ein eigener Upload-Knopf
         wird zu leicht übersehen.
       • Jede Zeile hat ein ✕ zum Entfernen, auch die fehlerfreien.
       • Eine fehlerhafte Datei BLOCKIERT das Absenden, bis der Nutzer sie entfernt. Das ist
         Absicht: so ist belegt, dass der Fehler gelesen wurde, statt ihn wegzuscrollen.
       • Der Uploadlink steht EINMAL unter dem Feld, nicht in jeder Zeile.
     Die eigentliche Schranke für Typ und Größe ist immer der SERVER; die Prüfung hier
     erspart dem Kunden nur den vergeblichen Weg. */

  function fileList(host) {
    return host.querySelector('[data-art-form-files]');
  }

  /** Zeichnet die Liste neu — einfacher und verlässlicher als Zeilen zu flicken. */
  function renderFiles(cfg, host) {
    const box = fileList(host);
    if (!box) return;
    box.innerHTML = '';
    box.hidden = cfg.files.length === 0;

    cfg.files.forEach((entry, i) => {
      const row = document.createElement('li');
      row.className = 'art-form__file';
      if (entry.error || entry.blocking || entry.failed) row.dataset.state = 'error';

      const name = document.createElement('span');
      name.className = 'art-form__file-name';
      name.textContent = entry.file.name;
      row.appendChild(name);

      const state = document.createElement('span');
      state.className = 'art-form__file-state';
      if (entry.blocking) {
        // Fehler UND was dagegen zu tun ist — ein Fehler ohne Ausweg ist eine Sackgasse.
        state.textContent = entry.error + (cfg.textMustRemove ? ' ' + cfg.textMustRemove : '');
      } else if (entry.failed) {
        // Hier ist NICHT zu entfernen, sondern nachzureichen — die Meldung unten sagt wie.
        state.textContent = entry.error || '';
      } else {
        state.textContent = entry.state || '';
      }
      row.appendChild(state);

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'art-form__file-remove';
      del.setAttribute('aria-label', (cfg.textRemove || 'Entfernen') + ': ' + entry.file.name);
      del.textContent = '\u00d7';
      del.addEventListener('click', () => {
        cfg.files.splice(i, 1);
        renderFiles(cfg, host);
        setStatus(host, '', 'info');
      });
      row.appendChild(del);

      box.appendChild(row);
    });
  }

  /* ── Uploadlink ──────────────────────────────────────────────────────────────────────
     Steht sichtbar unter dem Feld, erzeugt wird er aber erst bei der ersten Berührung
     (Fokus im Anhang-Bereich, Klick auf Kopieren oder Link, Dateiauswahl).

     ⚠️ Absicht: ein Filedrop ist ein SCHREIBRECHT ohne Anmeldung. Würde er bei jedem
     Seitenaufruf erzeugt, legte jeder Suchmaschinen-Roboter einen Ordner samt offenem
     Schreibrecht an — hunderte gleichzeitig gültige Token für nichts. So entsteht er nur,
     wenn ein Mensch den Bereich wirklich anfasst. */
  function ensureDrop(cfg, host) {
    if (cfg.dropPromise) return cfg.dropPromise;
    cfg.dropPromise = (async () => {
      try {
        const r = await fetch(cfg.endpoint + '/api/public/forms/' + cfg.shop + '/filedrop', {
          method: 'POST',
          mode: 'cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ idempotency_key: cfg.key() }),
        });
        const j = await r.json();
        if (!r.ok || !j || !j.ok || !j.url) {
          // Fehlschlag NICHT dauerhaft merken, sonst bleibt der Link für immer tot.
          cfg.dropPromise = null;
          return null;
        }
        cfg.filedrop = { url: j.url, expires: j.expires || undefined };
        const link = host.querySelector('[data-art-form-drop-link]');
        if (link) link.href = j.url;
        return j.url;
      } catch (e) {
        cfg.dropPromise = null;
        return null;
      }
    })();
    return cfg.dropPromise;
  }

  /** Grenzen und erlaubte Dateien beschriften — die Zahlen kommen vom Eingang. */
  function renderLimits(cfg, host) {
    const box = host.querySelector('[data-art-form-limits]');
    if (!box || !cfg.textLimits) return;
    const mb = cfg.maxBytes ? Math.floor(cfg.maxBytes / (1024 * 1024)) : '';
    box.textContent = cfg.textLimits
      .replace('{max}', String(mb))
      .replace('{types}', cfg.types.join(', '))
      .replace('{days}', String(cfg.dropDays || ''));
    box.hidden = false;
  }

  /* ⚠️ Die Trennung, auf der alles Weitere aufbaut:
       • VOR der Übertragung erkannt (falscher Typ, zu groß) → `blocking`. Der Kunde muss die
         Datei entfernen; damit ist belegt, dass er den Fehler gelesen hat.
       • WÄHREND der Übertragung passiert (Speicher weg, 413/415/429/502, Netz) → NICHT
         blockierend. An der Datei ist nichts falsch, die Störung ist unsere. Dann geht die
         Anfrage trotzdem raus und der Kunde bekommt statt der Erfolgsmeldung den Hinweis mit
         dem Uploadlink. Alles andere hiesse: eine fremde Störung kostet die Nachricht. */
  async function upload(cfg, entry) {
    const url =
      cfg.endpoint +
      '/api/public/forms/' +
      cfg.shop +
      '/upload?key=' +
      encodeURIComponent(cfg.key()) +
      '&name=' +
      encodeURIComponent(entry.file.name);

    let res;
    try {
      res = await fetch(url, { method: 'POST', mode: 'cors', body: entry.file });
    } catch (e) {
      entry.failed = true;
      entry.error = cfg.textUploadFailed;
      return false;
    }
    /* ⚠️ Der Ausgang steht im FLAG, nicht im Text: ein nicht gepflegter Wortlaut ist der
       leere String, und der ist falsy — die gescheiterte Datei hätte sonst als übertragen
       gegolten und wäre im Eintrag angekündigt worden, ohne je anzukommen. */
    if (res.status === 413) { entry.failed = true; entry.error = cfg.textTooBig; }
    else if (res.status === 415) { entry.failed = true; entry.error = cfg.textType; }
    else if (!res.ok) { entry.failed = true; entry.error = cfg.textUploadFailed; }
    if (entry.failed) return false;
    entry.state = cfg.textUploaded;
    return true;
  }

  /* ── Benachrichtigungs-Mail ─────────────────────────────────────────────────────────
     Die verschickt seit 02.10.2026 das DASHBOARD selbst — hier passiert nichts mehr.

     Vorgeschichte, damit es niemand erneut versucht: die Mail hing früher an Shopifys
     Kontaktformular, das zusätzlich abgeschickt wurde. Shopify schützt die
     Storefront-Formulare aber mit hCaptcha. Per `fetch` kam „Missing CAPTCHA token"
     (HTTP 400) zurück, weil Shopifys Skript den Token erst beim NATIVEN Absenden setzt;
     der Umweg über ein verstecktes `iframe` lief in Shopifys `/challenge`-Seite, die
     sich nicht rahmen lässt. Beides gemessen. Und selbst mit richtigem Zeitpunkt bliebe
     der Rest: verlangt Shopify einen Challenge, ist ein unsichtbarer Rahmen eine
     Sackgasse — die Mail bliebe aus, ohne dass es jemand merkt.

     Der eigene Versand kann ohnehin mehr: er kennt den Link auf den Eintrag und die
     Zahl der angenommenen Anhänge. Details im Dashboard unter `lib/formMail.ts`. */

  /* ── Bot-Abwehr: Cloudflare Turnstile ────────────────────────────────────────────────
     Der EIGENE Nachweis für den eigenen Eingang. Shopifys hCaptcha taugt dafür nicht:
     ein Token ist nur so viel wert, wie man ihn prüfen kann, und die Prüfung verlangt den
     Secret Key — der gehört Shopify. Unser Eingang bekäme eine undurchsichtige Zeichenkette,
     die er glauben müsste.

     Unsichtbar, solange Cloudflare den Besucher für harmlos hält: `execution: "execute"`
     führt die Prüfung erst auf Zuruf aus, `appearance: "interaction-only"` zeigt das
     Kästchen nur, wenn wirklich jemand klicken muss. */
  let TURNSTILE = null;
  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (TURNSTILE) return TURNSTILE;
    TURNSTILE = new Promise((resolve) => {
      const sc = document.createElement('script');
      sc.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      sc.async = true;
      sc.defer = true;
      sc.onload = () => resolve(window.turnstile || null);
      sc.onerror = () => resolve(null); // ohne Cloudflare läuft der Eingang ungeprüft weiter
      document.head.appendChild(sc);
    });
    return TURNSTILE;
  }

  /* Cloudflare verwirft ein Token nach 5 Minuten. Wir rechnen mit 4, damit ein im
     Voraus geholtes nicht genau auf der Ziellinie verfällt — dann lieber einmal frisch
     holen, als den Eintrag an einem abgelaufenen Token verlieren. */
  const TOKEN_HALTBAR_MS = 4 * 60 * 1000;

  /** Das vorgewärmte Token, solange es noch frisch ist. Sonst `null`. */
  function frischesToken(cfg) {
    const t = cfg.turnstileToken;
    return t && Date.now() - t.zeit < TOKEN_HALTBAR_MS ? t.wert : null;
  }

  /**
   * EIN Durchgang: Widget anlegen (oder zurücksetzen) und die Prüfung ausführen.
   * Gibt `undefined` zurück, wenn kein Sitekey eingetragen ist, das Skript nicht lädt
   * oder die Prüfung scheitert — dann entscheidet der Eingang.
   * ⚠️ Der Rückruf gehört dem WIDGET, nicht dem Aufruf: deshalb liegt der Auflöser in
   * `cfg.turnstileResolve` und wird je Durchgang neu gesetzt. Sonst hielte beim zweiten
   * Absenden das Versprechen des ersten. Aus demselben Grund darf immer nur EIN
   * Durchgang gleichzeitig laufen — darauf achten die beiden Funktionen darunter.
   */
  function turnstileLauf(cfg, host) {
    if (!cfg.turnstileKey) return Promise.resolve(undefined);
    const box = host.querySelector('[data-art-form-turnstile-box]');
    if (!box) return Promise.resolve(undefined);
    return loadTurnstile().then(
      (api) =>
        new Promise((resolve) => {
          if (!api) return resolve(undefined);
          let done = false;
          const finish = (v) => {
            if (done) return;
            done = true;
            cfg.turnstileResolve = null;
            resolve(v);
          };
          cfg.turnstileResolve = finish;
          // Nicht ewig warten: der Eintrag darf an einer hängenden Prüfung nicht scheitern.
          setTimeout(() => finish(undefined), 20000);
          try {
            if (cfg.turnstileWidget == null) {
              cfg.turnstileWidget = api.render(box, {
                sitekey: cfg.turnstileKey,
                execution: 'execute',
                appearance: 'interaction-only',
                callback: (tok) => cfg.turnstileResolve && cfg.turnstileResolve(tok),
                'error-callback': () => cfg.turnstileResolve && cfg.turnstileResolve(undefined),
                'timeout-callback': () => cfg.turnstileResolve && cfg.turnstileResolve(undefined),
              });
            } else {
              api.reset(cfg.turnstileWidget);
            }
            api.execute(cfg.turnstileWidget);
          } catch (e) {
            finish(undefined);
          }
        })
    );
  }

  /**
   * ⭐ Vorwärmen — die Prüfung läuft beim ersten Tippen, nicht erst beim Klick auf
   * „Senden" (Vorgabe Alex, 07.10.2026). Zwischen Klick und Erfolgsmeldung lagen rund
   * drei Sekunden; beim Tippen kostet dasselbe niemanden Wartezeit.
   *
   * ⚠️ Vorgezogen werden muss der ganze DURCHGANG, nicht bloß das Anlegen des Widgets:
   * an der Live-Storefront gemessen (07.10.2026) braucht das Skript 36 ms und das
   * Widget 2 ms — die Zeit steckt fast vollständig in `execute()`, also in Cloudflares
   * Prüfung selbst.
   *
   * Nebenwirkung, bewusst in Kauf genommen: hält Cloudflare den Besucher für
   * verdächtig, erscheint das Kästchen jetzt schon beim Ausfüllen statt erst beim
   * Absenden. Das ist die bessere Reihenfolge — vorher lief es in den Zeitablauf und
   * der Eintrag fiel weg.
   *
   * Entscheidet selbst, ob es etwas zu tun gibt; darf deshalb bei jedem Tastendruck
   * gerufen werden.
   */
  function turnstileVorwaermen(cfg, host) {
    if (!cfg.turnstileKey || cfg.turnstilePrime || frischesToken(cfg)) return;
    cfg.turnstilePrime = turnstileLauf(cfg, host).then((tok) => {
      cfg.turnstilePrime = null;
      cfg.turnstileToken = tok ? { wert: tok, zeit: Date.now() } : null;
    });
  }

  /** Das Token fürs Absenden: das vorgewärmte, sonst ein frisch geholtes. */
  async function turnstileToken(cfg, host) {
    if (!cfg.turnstileKey) return undefined;
    /* Läuft gerade ein Durchgang, darauf WARTEN statt einen zweiten zu starten — zwei
       gleichzeitig nähmen einander den Auflöser weg, und der erste hinge bis zu seinem
       Zeitablauf. Schlimmstenfalls wartet man hier so lange wie früher. */
    if (cfg.turnstilePrime) await cfg.turnstilePrime;
    const bereit = frischesToken(cfg);
    // ⚠️ Ein Token ist EINMAL einlösbar — mit der Entnahme ist es verbraucht. Bliebe es
    // liegen, bekäme das nächste Absenden ein `timeout-or-duplicate` und damit 403.
    cfg.turnstileToken = null;
    return bereit || turnstileLauf(cfg, host);
  }

  /* ── Absenden ───────────────────────────────────────────────────────────────────────── */
  async function send(cfg, host, form) {
    const data = collect(form);
    /* Nachweise für die Bot-Abwehr des Eingangs. Honigtopf und Mindestzeit gab es
       hier schon, aber sie wirkten NUR im Browser — wer direkt auf die Route postet,
       sah beide nie. Deshalb wandern sie mit und werden dort geprüft. */
    const hp = form.querySelector('[data-art-form-honeypot]');
    const token = await turnstileToken(cfg, host);
    const body = {
      form: cfg.kind,
      idempotency_key: cfg.key(),
      fields: data.fields,
      roles: data.roles,
      attachments: cfg.files
        .filter((e) => !e.blocking && !e.failed)
        .map((e) => ({ name: e.file.name })),
      filedrop: cfg.filedrop,
      consent: data.consent,
      turnstile: token,
      hp: hp ? hp.value : '',
      elapsed_ms: Date.now() - cfg.born,
      meta: {
        page: location.pathname,
        locale: document.documentElement.lang || '',
        ts: Date.now(),
      },
    };

    const res = await fetch(cfg.endpoint + '/api/public/forms/' + cfg.shop, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    // 201 = neu, 200 = derselbe Schlüssel (Doppelklick) — beides ist Erfolg.
    if (!res.ok) throw new Error('endpoint ' + res.status);
    const j = await res.json().catch(() => null);
    if (!j || !j.ok) throw new Error('endpoint payload');
    return j;
  }

  function init(host) {
    if (HOSTS.has(host)) return;
    const form = host.querySelector('form');
    const endpoint = t(host, 'artFormEndpoint').replace(/\/+$/, '');
    const shop = t(host, 'artFormShop');
    if (!form || !endpoint || !shop) return; // ohne Vertrag bleibt alles wie es ist
    HOSTS.add(host);

    let key = newKey();
    const cfg = {
      kind: t(host, 'artForm', 'contact'),
      endpoint,
      shop,
      key: () => key,
      types: t(host, 'artFormTypes')
        .toLowerCase()
        .split(/[^a-z0-9]+/)
        .filter(Boolean),
      maxBytes: (parseFloat(t(host, 'artFormMaxMb', '0')) || 0) * 1024 * 1024,
      textUploading: t(host, 'artFormTextUploading'),
      textUploaded: t(host, 'artFormTextUploaded'),
      textUploadFailed: t(host, 'artFormTextUploadFailed'),
      textType: t(host, 'artFormTextType'),
      textTooBig: t(host, 'artFormTextTooBig'),
      textTooBigLink: t(host, 'artFormTextTooBigLink'),
      textSuccess: t(host, 'artFormTextSuccess'),
      textLimits: t(host, 'artFormTextLimits'),
      textMustRemove: t(host, 'artFormTextMustRemove'),
      textRemove: t(host, 'artFormTextRemove'),
      textBlocked: t(host, 'artFormTextBlocked'),
      textPartial: t(host, 'artFormTextPartial'),
      /* Ausweichweg, falls der Uploadlink nicht geht (Nextcloud gestört, Firmennetz sperrt
         die Domain, Link abgelaufen). Reine Store-Angabe: ohne URL entsteht nichts. */
      altUrl: t(host, 'artFormAltUrl'),
      textAlt: t(host, 'artFormTextAlt'),
      textAltLink: t(host, 'artFormTextAltLink'),
      textCopied: t(host, 'artFormTextCopied'),
      filedrop: undefined,
      dropPromise: null,
      dropDays: 0,
      /* Ausgewählte Dateien: { file, state, error }. Hochgeladen wird erst beim Absenden. */
      files: [],
      /* Bot-Abwehr: leer = aus (dann prüft nur der Eingang mit seinen übrigen Riegeln). */
      turnstileKey: t(host, 'artFormTurnstile'),
      turnstileWidget: null,
      turnstileResolve: null,
      /* Vorgewärmtes Token { wert, zeit } und der laufende Durchgang — siehe
         turnstileVorwaermen(). */
      turnstileToken: null,
      turnstilePrime: null,
      born: 0,
    };

    const born = Date.now();
    cfg.born = born;
    const minMs = parseInt(t(host, 'artFormMinMs', '1500'), 10) || 0;
    let enhanced = false;
    let busy = false;

    /* Erst freischalten, wenn der Endpunkt WIRKLICH antwortet. Alles, was der
       Shopify-Rückfall nicht übertragen kann (heute: der Datei-Anhang), bleibt bis dahin
       unsichtbar — sonst verschluckt ein Feld die Datei des Kunden. */
    health(endpoint).then((info) => {
      if (!info) return;
      enhanced = true;
      /* Grenzen aus der Antwort übernehmen — die Theme-Einstellung ist nur noch ein
         Rückfall, falls eine ältere Fassung des Eingangs sie nicht mitliefert. */
      if (info.maxUploadBytes) cfg.maxBytes = info.maxUploadBytes;
      if (Array.isArray(info.allowedExtensions) && info.allowedExtensions.length) {
        cfg.types = info.allowedExtensions.map((e) => String(e).toLowerCase());
      }
      cfg.dropDays = info.filedropDays || 0;
      host.querySelectorAll('[data-art-form-enhanced-only]').forEach((el) => {
        el.hidden = false;
      });
      renderLimits(cfg, host);
    });

    /* ⭐ Bot-Prüfung anstoßen, sobald jemand das Formular anfasst — nicht erst beim
       Klick auf „Senden". Siehe turnstileVorwaermen().

       Bewusst NICHT `{ once: true }`: mit dem Absenden ist das Token verbraucht, dann
       muss der nächste Tastendruck wieder vorwärmen. `turnstileVorwaermen` prüft
       selbst, ob etwas zu tun ist, und kostet sonst nichts.

       `enhanced` als Bedingung, weil das Token nur UNSEREN Eingang interessiert:
       antwortet der nicht, läuft ohnehin der native Weg und die Prüfung wäre vergeblich
       (und für Cloudflare ein Durchgang ohne Zweck). */
    const vorwaermen = () => {
      if (enhanced) turnstileVorwaermen(cfg, host);
    };
    form.addEventListener('input', vorwaermen);
    form.addEventListener('change', vorwaermen); // Auswahlfeld, Kästchen, Datei

    const fileInput = host.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.addEventListener('change', () => {
        if (!enhanced) return;
        const picked = [...(fileInput.files || [])];
        fileInput.value = '';
        for (const f of picked) {
          const ext = (f.name.split('.').pop() || '').toLowerCase();
          const entry = { file: f, state: '', error: '', blocking: false, failed: false };
          // Nur die beiden Prüfungen, die OHNE Übertragung möglich sind.
          if (cfg.types.length && cfg.types.indexOf(ext) === -1) {
            entry.error = cfg.textType;
            entry.blocking = true;
          } else if (cfg.maxBytes && f.size > cfg.maxBytes) {
            entry.error = cfg.textTooBig;
            entry.blocking = true;
          }
          cfg.files.push(entry);
        }
        renderFiles(cfg, host);
        // Den Uploadlink schon bereitlegen: wer Dateien wählt, braucht ihn womöglich.
        void ensureDrop(cfg, host);
      });
    }

    /* Uploadlink erst bei Berührung erzeugen — siehe ensureDrop(). */
    const uploadArea = host.querySelector('[data-art-form-enhanced-only="upload"]');
    if (uploadArea) {
      uploadArea.addEventListener('focusin', () => {
        if (enhanced) void ensureDrop(cfg, host);
      });
    }

    const dropLink = host.querySelector('[data-art-form-drop-link]');
    if (dropLink) {
      dropLink.addEventListener('click', async (ev) => {
        /* ⚠️ Gültig ist der Link NUR, solange `cfg.filedrop` ihn hält — nicht, weil im `href`
           irgendetwas steht. Nach einem Absenden gehört ein dort stehengebliebener Link zur
           VORIGEN Anfrage; ihm zu folgen legte die Datei in den Ordner der alten Anfrage.
           Deshalb entscheidet der Zustand, nicht das Attribut. */
        if (cfg.filedrop && cfg.filedrop.url) return; // gehört zu dieser Sitzung
        ev.preventDefault();
        const url = await ensureDrop(cfg, host);
        if (url) window.open(url, '_blank', 'noopener');
      });
    }

    const dropCopy = host.querySelector('[data-art-form-drop-copy]');
    if (dropCopy) {
      dropCopy.addEventListener('click', async () => {
        const url = await ensureDrop(cfg, host);
        if (!url) return;
        try {
          await navigator.clipboard.writeText(url);
          const before = dropCopy.getAttribute('aria-label');
          dropCopy.dataset.copied = 'true';
          if (cfg.textCopied) dropCopy.setAttribute('aria-label', cfg.textCopied);
          setTimeout(() => {
            delete dropCopy.dataset.copied;
            if (before) dropCopy.setAttribute('aria-label', before);
          }, 2000);
        } catch (e) {
          /* Ohne Zwischenablage bleibt der Link zum Anklicken da. */
        }
      });
    }

    form.addEventListener('submit', (ev) => {
      if (!enhanced || busy) return; // nativer Weg
      if (!form.reportValidity()) return;

      /* Spam-Verdacht → NATIVER Weg, nicht blockieren. Ein Mensch, dessen Browser das
         versteckte Feld ausgefüllt hat, verliert so seine Nachricht nicht; Bots landen in
         Shopifys eigener Prüfung. Der Preis ist ein fehlender Dashboard-Eintrag, und das ist
         das kleinere Übel. */
      const hp = form.querySelector('[data-art-form-honeypot]');
      if (hp && hp.value) return;
      if (minMs && Date.now() - born < minMs) return;

      /* Fehlerhafte Datei? Dann NICHT absenden — auch nicht nativ. Der Nutzer muss sie
         entfernen; damit ist belegt, dass er den Fehler gesehen hat. Würde hier still auf
         den Shopify-Weg gewechselt, käme die Anfrage ohne Datei an und niemand wüsste warum. */
      ev.preventDefault();

      /* ⛔ Das Ereignis hier ANHALTEN, nicht nur die Standardaktion. Shopifys Bot-Schutz
         (`captcha-bootstrap`, geladen auf jeder Seite mit einem Shopify-Formular) hängt
         einen ZWEITEN `submit`-Horcher an das `document` und ruft darin `form.submit()` —
         ein natives Absenden, das `preventDefault()` nicht verhindert: das unterbindet die
         Standardaktion, nicht weitere Horcher. Ohne diese Zeile erscheint die
         Erfolgsmeldung und rund 300 ms später navigiert die Seite weg; weil die `action`
         des Formular-Tags den Anker des Formulars trägt, springt der Browser dabei nach
         oben und scrollt zurück, und die Meldung ist fort (belegt am Stack:
         ce_storefront_forms_captcha_hcaptcha).
         Nur in DIESEM Zweig anhalten: läuft der native Weg (kein Endpunkt, Honigtopf, zu
         schnell abgeschickt), kehrt der Horcher vorher mit `return` zurück — dort behält
         Shopifys Schutz seine Arbeit. */
      ev.stopImmediatePropagation();
      if (cfg.files.some((e) => e.blocking)) {
        setStatus(host, cfg.textBlocked, 'error');
        renderFiles(cfg, host);
        return;
      }

      busy = true;
      const btn = form.querySelector('[type="submit"]');
      /* Gesperrt UND sichtbar beschäftigt: zwischen Klick und Erfolgsmeldung liegen
         rund drei Sekunden (Bot-Prüfung, Upload, Eintrag). Ohne Zeichen wirkt das wie
         ein toter Klick. `aria-busy` sagt dasselbe für Vorlesewerkzeuge. */
      const busyAn = (an) => {
        if (!btn) return;
        btn.disabled = an;
        btn.classList.toggle('art-form__busy', an);
        if (an) btn.setAttribute('aria-busy', 'true');
        else btn.removeAttribute('aria-busy');
      };
      busyAn(true);
      setStatus(host, '', 'info');

      (async () => {
        /* Erst die Dateien, dann der Eintrag — sonst hätte der Eintrag nichts zu übernehmen.
           Nacheinander, nicht parallel: bei 90-MB-Dateien ist gleichzeitig nichts gewonnen,
           und der Fortschritt bleibt ablesbar. */
        for (const entry of cfg.files) {
          if (entry.blocking) continue;
          entry.state = cfg.textUploading;
          renderFiles(cfg, host);
          await upload(cfg, entry);
          renderFiles(cfg, host);
        }
        /* Bewusst KEIN Abbruch mehr, wenn hier etwas schiefging: die Datei ist in Ordnung,
           die Störung war unsere. Der Eintrag geht raus (er trägt den Uploadlink mit), und
           die Meldung unten nennt die fehlenden Dateien. `attachments` im Rumpf listet
           ohnehin nur, was wirklich durchkam — der Eingang glaubt der Liste nicht, sondern
           prüft selbst nach. */
        return send(cfg, host, form);
      })()
        .then((result) => {
          if (!result) {
            /* Abgebrochen, die Meldung steht schon. Trotzdem freigeben: sonst bliebe der
               Knopf gesperrt und der Punkt drehte sich endlos. */
            busy = false;
            busyAn(false);
            return;
          }
          /* Daten einsammeln, SOLANGE das Formular gefüllt ist — danach zurücksetzen und den
             Erfolg melden. Der Mailversand läuft im Hintergrund weiter.

             ⚠️ Eigenes try/catch: der Eintrag STEHT an dieser Stelle. Ein Fehler im
             Benachrichtigungsweg darf nicht im `catch` unten landen, denn der schickt das
             Formular nativ ab — der Kunde verlöre seine Erfolgsmeldung für ein Problem, das
             ihn nicht betrifft. */
          /* Was NICHT angekommen ist — aus ZWEI Quellen, weil jede für sich eine Lücke hat:
             die eigenen Upload-Fehler kennen die Datei, aber nicht das Urteil des Eingangs;
             `rejected` kennt das Urteil, aber nur für Dateien, die es überhaupt hinschafften
             (der Eingang prüft beim Übernehmen ein zweites Mal). */
          const failed = cfg.files.filter((e) => e.failed).map((e) => e.file.name);
          const rejected = (result.attachments && result.attachments.rejected) || [];
          const missing = [];
          for (const n of failed.concat(rejected)) if (missing.indexOf(n) === -1) missing.push(n);

          if (missing.length) {
            /* Kein „Angekommen." über einer verlorenen Datei. Der Eintrag STEHT, deshalb keine
               Fehlerfarbe — aber der Kunde erfährt, welche Datei fehlt, und bekommt den
               Uploadlink gleich mit. Der Link gehört zu genau diesem Eintrag: der Eingang
               verschiebt die Freigabe mit dem Ordner, ein späterer Upload landet also richtig. */
            setStatus(
              host,
              (cfg.textPartial || cfg.textSuccess).replace('{files}', missing.join(', ')),
              'warn',
              [
                { url: cfg.filedrop && cfg.filedrop.url, label: cfg.textTooBigLink },
                // Zweiter Weg, falls der erste nicht geht — mit eigenem Hinweis davor.
                { url: cfg.altUrl, label: cfg.textAltLink, prefix: cfg.textAlt },
              ]
            );
          } else {
            setStatus(host, cfg.textSuccess, 'ok');
          }
          /* Für Blöcke, die nach dem Eintrag etwas zeigen (die Anmeldung zu Angeboten per
             E-Mail im Angebotsformular): VOR dem Zurücksetzen, solange die Adresse noch im
             Feld steht. ⚠️ In `try`: ein Fehler hier liefe sonst in den `catch` unten, und
             der schickt die Anfrage nativ ein zweites Mal ab. */
          try {
            const mail = form.querySelector('[data-art-form-role="email"]');
            host.dispatchEvent(
              new CustomEvent('art-form:sent', {
                bubbles: true,
                detail: { kind: cfg.kind, email: mail ? String(mail.value || '').trim() : '' },
              })
            );
          } catch (e) {
            /* Melden ist Zugabe, der Eintrag steht. */
          }
          form.reset();
          cfg.files = [];
          renderFiles(cfg, host);
          /* Nächste Anfrage = neue Sitzung: neuer Schlüssel, neuer Ordner, neuer Link.
             Der sichtbare `href` MUSS mit zurück auf '#' — sonst zeigt er weiter auf den
             Ordner der gerade abgeschickten Anfrage, und die nächste Datei landete dort.
             In der Teilerfolg-Meldung steht der alte Link als eigene Kopie; dort ist er
             richtig, weil die Datei ja zu genau jener Anfrage gehört. */
          key = newKey();
          cfg.dropPromise = null;
          cfg.filedrop = undefined;
          if (dropLink) dropLink.setAttribute('href', '#');
          busy = false;
          busyAn(false);
        })
        .catch(() => {
          /* Endpunkt weg oder Fehler: nativ abschicken. Die Anfrage kommt dann per Mail an
             — Datenverlust gibt es nicht, nur keinen Eintrag. */
          busy = false;
          busyAn(false);
          form.submit();
        });
    });
  }

  const scan = (scope) => (scope || document).querySelectorAll('[data-art-form]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }
  // Theme-Editor: Sektionen werden einzeln neu geladen.
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
})();
